import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  BookOpen, Plus, Search, Loader2, Edit2, Trash2, Eye, EyeOff, Sparkles, History, RotateCcw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BLANK = { title: '', slug: '', category: 'General', summary: '', body: '', tags: '', is_published: true };

export default function AdminKnowledgeBase() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // article doc when editing
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // AI draft state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ title: '', outline: '', tone: 'professional, friendly, actionable' });
  const [aiBusy, setAiBusy] = useState(false);

  // Versions state
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsBusy, setVersionsBusy] = useState(false);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/knowledge-base`, { params: q ? { q } : {}, withCredentials: true });
      setItems(res.data.items || []);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setModalOpen(true);
  };

  const openEdit = async (article) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/knowledge-base/${article.article_id}`, { withCredentials: true });
      setEditing(res.data);
      setForm({
        title: res.data.title || '',
        slug: res.data.slug || '',
        category: res.data.category || 'General',
        summary: res.data.summary || '',
        body: res.data.body || '',
        tags: (res.data.tags || []).join(', '),
        is_published: !!res.data.is_published,
      });
      setModalOpen(true);
    } catch (err) {
      toast.error('Failed to load article');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      category: form.category.trim() || 'General',
      summary: form.summary.trim(),
      body: form.body,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      is_published: !!form.is_published,
    };
    try {
      if (editing) {
        await axios.patch(`${API_URL}/api/admin/knowledge-base/${editing.article_id}`, payload, { withCredentials: true });
        toast.success('Article updated');
      } else {
        await axios.post(`${API_URL}/api/admin/knowledge-base`, payload, { withCredentials: true });
        toast.success('Article created');
      }
      setModalOpen(false);
      load(search.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/admin/knowledge-base/${article.article_id}`, { withCredentials: true });
      toast.success('Article deleted');
      load(search.trim());
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (article) => {
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/admin/knowledge-base/${article.article_id}`, { is_published: !article.is_published }, { withCredentials: true });
      toast.success(article.is_published ? 'Unpublished' : 'Published');
      load(search.trim());
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const runAiDraft = async () => {
    if (!aiForm.title.trim()) {
      toast.error('Give the draft a title first');
      return;
    }
    setAiBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/knowledge-base/ai-draft`, aiForm, { withCredentials: true });
      // Pre-fill the editor with the AI draft (unpublished by default)
      setEditing(null);
      setForm({
        title: res.data.title || aiForm.title,
        slug: '',
        category: 'AI Drafts',
        summary: res.data.summary || '',
        body: res.data.body || '',
        tags: 'ai-draft',
        is_published: false,
      });
      setAiOpen(false);
      setModalOpen(true);
      toast.success('AI draft ready - review and save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI draft failed');
    } finally {
      setAiBusy(false);
    }
  };

  const loadVersions = async (article) => {
    setVersionsFor(article);
    setVersionsBusy(true);
    setVersions([]);
    try {
      const res = await axios.get(`${API_URL}/api/admin/knowledge-base/${article.article_id}/versions`, { withCredentials: true });
      setVersions(res.data.versions || []);
    } catch (err) {
      toast.error('Failed to load versions');
    } finally {
      setVersionsBusy(false);
    }
  };

  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore version from ${v.created_at?.slice(0,16)}? Current content will be snapshotted first.`)) return;
    setVersionsBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/knowledge-base/${versionsFor.article_id}/versions/${v.version_id}/restore`, {}, { withCredentials: true });
      toast.success('Version restored');
      setVersionsFor(null);
      load(search.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setVersionsBusy(false);
    }
  };

  return (
    <AdminLayout title="Knowledge Base">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-kb-header">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><BookOpen size={22} /> Knowledge Base</h1>
          <p className="text-sm text-[#475569] mt-1">Articles published here are searchable at <a className="text-[#1D4ED8] hover:underline" href="/knowledge-base">/knowledge-base</a>.</p>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={(e) => { e.preventDefault(); load(search.trim()); }} className="flex items-center gap-2">
            <div className="relative">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title / category / tags…" className="pl-9 w-72" data-testid="admin-kb-search-input" />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            </div>
            <Button type="submit" variant="outline" data-testid="admin-kb-search-btn">Search</Button>
          </form>
          <Button onClick={() => { setAiForm({ title: '', outline: '', tone: 'professional, friendly, actionable' }); setAiOpen(true); }} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="admin-kb-ai-btn">
            <Sparkles size={14} className="mr-1.5" /> AI draft
          </Button>
          <Button onClick={openNew} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="admin-kb-new-btn">
            <Plus size={14} className="mr-1.5" /> New article
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        {/* Desktop / tablet: full table */}
        <div className="hidden md:block">
          <table className="w-full text-sm" data-testid="admin-kb-table">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Views</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">No articles yet. Create your first one.</td></tr>
              ) : items.map((a) => (
                <tr key={a.article_id} className="hover:bg-[#F8FAFC]" data-testid={`admin-kb-row-${a.article_id}`}>
                  <td className="px-4 py-3 font-medium text-[#0A0F1C]">
                    {a.title}
                    <div className="text-[10px] text-[#94A3B8] font-mono mt-0.5">/{a.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-[#475569]"><Badge className="bg-[#F1F5F9] text-[#475569]">{a.category}</Badge></td>
                  <td className="px-4 py-3 text-xs text-[#475569]">{(a.tags || []).join(', ') || '-'}</td>
                  <td className="px-4 py-3">
                    {a.is_published ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><Eye size={12} /> Published</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#94A3B8]"><EyeOff size={12} /> Draft</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#475569]">{a.views || 0}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">{a.updated_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => togglePublish(a)} disabled={busy} data-testid={`admin-kb-toggle-${a.article_id}`}>
                      {a.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => loadVersions(a)} disabled={busy} data-testid={`admin-kb-versions-${a.article_id}`} title="Version history">
                      <History size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} disabled={busy} data-testid={`admin-kb-edit-${a.article_id}`}><Edit2 size={12} /></Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(a)} disabled={busy} data-testid={`admin-kb-delete-${a.article_id}`}><Trash2 size={12} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: stacked cards */}
        <div className="md:hidden divide-y divide-[#F1F5F9]" data-testid="admin-kb-mobile-list">
          {loading ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">No articles yet. Create your first one.</div>
          ) : items.map((a) => (
            <div key={a.article_id} className="p-4 space-y-2" data-testid={`admin-kb-card-${a.article_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0A0F1C] truncate">{a.title}</div>
                  <div className="text-[10px] text-[#94A3B8] font-mono truncate">/{a.slug}</div>
                </div>
                {a.is_published ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 whitespace-nowrap"><Eye size={10} /> Published</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#94A3B8] whitespace-nowrap"><EyeOff size={10} /> Draft</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#475569]">
                <Badge className="bg-[#F1F5F9] text-[#475569]">{a.category}</Badge>
                {(a.tags || []).length > 0 && <span>· {(a.tags || []).join(', ')}</span>}
                <span className="ml-auto text-[#94A3B8]">{a.views || 0} views · {a.updated_at?.slice(0, 10)}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => togglePublish(a)} disabled={busy}>
                  {a.is_published ? 'Unpublish' : 'Publish'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadVersions(a)} disabled={busy}><History size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} disabled={busy}><Edit2 size={12} className="mr-1" /> Edit</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(a)} disabled={busy}><Trash2 size={12} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {categories.length > 0 && (
        <p className="text-xs text-[#94A3B8] mt-4">Categories in use: {categories.join(' · ')}</p>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="admin-kb-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit article' : 'New article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Title *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-kb-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Slug (optional)</label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" data-testid="admin-kb-form-slug" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Category *</label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} list="kb-categories" data-testid="admin-kb-form-category" />
                <datalist id="kb-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Summary</label>
              <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="One-sentence teaser shown in lists" data-testid="admin-kb-form-summary" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Body * (Markdown or HTML)</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={10}
                className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 text-sm font-mono"
                data-testid="admin-kb-form-body"
              />
              {editing && (
                <div className="mt-2 flex items-center justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const instruction = window.prompt('Optional steer for the AI (leave blank for a general tighten + improve):') || '';
                      if (!window.confirm('AI will rewrite this article. A version snapshot will be taken first so you can restore it. Continue?')) return;
                      setBusy(true);
                      try {
                        const res = await axios.post(`${API_URL}/api/admin/knowledge-base/${editing.article_id}/ai-improve`, { instruction }, { withCredentials: true });
                        setForm({ ...form, body: res.data.body || form.body, summary: res.data.summary || form.summary, tags: (res.data.tags || []).join(', ') || form.tags });
                        toast.success('AI rewrite applied - review and save');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'AI improve failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    data-testid="admin-kb-improve-btn"
                    className="text-violet-700 border-violet-200 hover:bg-violet-50"
                  >
                    <Sparkles size={12} className="mr-1.5" /> Improve with AI
                  </Button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Tags (comma-separated)</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="getting-started, billing, faq" data-testid="admin-kb-form-tags" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="admin-kb-form-published" />
              <span>Published (visible at /knowledge-base)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="admin-kb-save-btn">
              {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : (editing ? 'Save changes' : 'Create article')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Draft dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-kb-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-violet-600" /> AI article writer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#475569]">Generates a first markdown draft using Claude Sonnet 4.6 via your Emergent LLM key. The draft opens in the editor - review and publish when ready.</p>
            <div>
              <label htmlFor="ai-title" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Article title <span className="text-red-500">*</span></label>
              <Input id="ai-title" value={aiForm.title} onChange={(e) => setAiForm({ ...aiForm, title: e.target.value })} placeholder="e.g. How to design a 90-day career sprint" data-testid="admin-kb-ai-title" required aria-required="true" />
            </div>
            <div>
              <label htmlFor="ai-outline" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Outline (optional)</label>
              <textarea
                id="ai-outline"
                value={aiForm.outline}
                onChange={(e) => setAiForm({ ...aiForm, outline: e.target.value })}
                rows={4}
                className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 text-sm"
                placeholder={'- Pick the right outcome\n- Set weekly milestones\n- Build a review cadence'}
                data-testid="admin-kb-ai-outline"
              />
            </div>
            <div>
              <label htmlFor="ai-tone" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Tone</label>
              <Input id="ai-tone" value={aiForm.tone} onChange={(e) => setAiForm({ ...aiForm, tone: e.target.value })} data-testid="admin-kb-ai-tone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button onClick={runAiDraft} disabled={aiBusy || !aiForm.title.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="admin-kb-ai-generate-btn">
              {aiBusy ? <><Loader2 size={14} className="animate-spin mr-2" /> Drafting (10-30s)…</> : <><Sparkles size={14} className="mr-2" /> Generate draft</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsFor} onOpenChange={(o) => !o && setVersionsFor(null)}>
        <DialogContent className="max-w-xl" data-testid="admin-kb-versions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History size={16} /> Version history - {versionsFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {versionsBusy && <div className="text-center py-6 text-[#94A3B8]"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</div>}
            {!versionsBusy && versions.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-4">No prior versions yet. Versions are snapshotted automatically on every edit.</p>}
            {versions.map((v) => (
              <div key={v.version_id} className="border border-[#E2E8F0] rounded-lg p-3 bg-[#F8FAFC]" data-testid={`admin-kb-version-${v.version_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="text-xs font-bold text-[#0A0F1C]">{v.label || 'Snapshot'}</div>
                  <Button size="sm" variant="outline" onClick={() => restoreVersion(v)} disabled={versionsBusy} data-testid={`admin-kb-version-restore-${v.version_id}`}>
                    <RotateCcw size={12} className="mr-1.5" /> Restore
                  </Button>
                </div>
                <div className="text-[10px] text-[#94A3B8] mb-2">{v.author_email || 'system'} · {v.created_at?.slice(0, 19).replace('T', ' ')}</div>
                <div className="text-xs text-[#475569] line-clamp-2">{v.snapshot?.title}: {(v.snapshot?.summary || v.snapshot?.body || '').slice(0, 160)}…</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
